import { merge } from '../../../helper';

import type { CustomFile } from '../types';

import type { TypeUploadConfig, TypeUploadResult, TypeUploadResultPromise } from './types';
import type { TypeUploadState } from './createUploadController';
import sendSequentialFiles from './sendSequentialFiles';
import sendUpload from './sendUpload';

// ----------------------------------------------------------------------------

export type TypeSequentialGroup = {
    files: CustomFile[];
    config: TypeUploadConfig;
};

export type TypeSendSequentialGroupsOptions = {
    gateOn?: 'complete' | 'firstFile';
    fnAbortGroup?: (xhr: XMLHttpRequest) => boolean;
    // Files within a group upload sequentially by default (1). Set > 1 to send the
    // group's first file alone, then the rest at this concurrency once it lands.
    withinGroupConcurrency?: number;
    // Caps how many groups upload at the same time. The gate only staggers group
    // *starts*, so without a cap every started group stays in flight. Unlimited when omitted.
    maxGroupConcurrency?: number;
};

type TypeGroupSlots = {
    acquire: () => Promise<void>;
    release: () => void;
};

// ----------------------------------------------------------------------------

/*
 * Semaphore bounding how many groups upload concurrently (limit 0 = unlimited).
 * A released slot is handed straight to the next waiter, so the active count only
 * drops when nobody is queued.
 */
function createGroupSlots(limit: number): TypeGroupSlots {
    const waiters: (() => void)[] = [];
    let active = 0;

    return {
        acquire: async function (): Promise<void> {
            if (limit <= 0 || active < limit) {
                active += 1;
                return;
            }

            await new Promise<void>((resolve) => {
                waiters.push(resolve);
            });
        },
        release: function (): void {
            const next = waiters.shift();

            if (next) {
                next();
                return;
            }

            active -= 1;
        },
    };
}

type TypeGateConfigDeps = {
    openGate: () => void;
    fnAbortGroup?: (xhr: XMLHttpRequest) => boolean;
    getController: () => TypeUploadResultPromise['controller'] | null;
};

/*
 * Wraps a group's config so the gate opens as soon as its first file settles. The caller's
 * `merge` has to be resolved into `config` before this runs: merge outranks plain keys, so a
 * consumer's merge would otherwise silently replace the very hooks the gate depends on.
 *
 * The request-level hooks open the gate the moment the first response lands. The per-file ones
 * are the backstop for outcomes that never reach the wire — a file dropped by `fnFilter`, or one
 * whose body could not be prepared — where no request-level hook fires at all and the gate would
 * otherwise stay shut until the whole group had finished.
 */
function createGateConfig(config: TypeUploadConfig, deps: TypeGateConfigDeps): TypeUploadConfig {
    const { openGate, getController } = deps;
    const fnFilter = config.fnFilter;

    return {
        ...config,
        fnSuccess: (data, status, xhr) => {
            openGate();

            config.fnSuccess?.(data, status, xhr);
        },
        fnFail: (xhr, status) => {
            openGate();

            if (deps.fnAbortGroup?.(xhr) === true) {
                getController()?.abort();
            }

            config.fnFail?.(xhr, status);
        },
        /*
         * Network errors and timeouts never reach fnFail. Without this the gate would stay shut
         * until the whole group had finished retrying, serializing every remaining group behind
         * a single transient failure.
         */
        fnError: (evt, status) => {
            openGate();

            config.fnError?.(evt, status);
        },
        fnFileSuccess: (file, xhr) => {
            openGate();

            config.fnFileSuccess?.(file, xhr);
        },
        fnFileError: (file, err) => {
            openGate();

            config.fnFileError?.(file, err);
        },
        fnFilter: fnFilter
            ? async (file): Promise<boolean> => {
                  const keep = await fnFilter(file);

                  if (!keep) {
                      openGate();
                  }

                  return keep;
              }
            : undefined,
    };
}

// A group aborted before it started reached none of its files.
function createAbortedResult(files: CustomFile[]): TypeUploadResult {
    return {
        success: [],
        error: [],
        skipped: [],
        notAttempted: files.map((file) => ({ file })),
        aborted: true,
    };
}

// ----------------------------------------------------------------------------

/*
 * Uploads independent groups of files (e.g. one group per study) one after another, each
 * group delegated to sequential or limited mode. The gate decides when the next group may
 * start: on the current group finishing, or — with `gateOn: 'firstFile'` — as soon as its
 * first file settles, which overlaps groups while still letting the server commit each
 * group's first write before the rest of that group fans out.
 *
 * Each returned controller is a proxy. A group that has not started has no inner controller
 * yet, so abort/pause requests are recorded and applied when its turn comes — an aborted
 * group must never send a single request.
 */
function sendSequentialGroups(
    groups: TypeSequentialGroup[],
    options?: TypeSendSequentialGroupsOptions,
): TypeUploadResultPromise[] {
    const gateOn = options?.gateOn ?? 'complete';
    const withinGroupConcurrency = Math.max(1, options?.withinGroupConcurrency ?? 1);
    const slots = createGroupSlots(Math.max(0, options?.maxGroupConcurrency ?? 0));
    const results: TypeUploadResultPromise[] = [];
    let gate: Promise<void> = Promise.resolve();

    for (const group of groups) {
        let innerController: TypeUploadResultPromise['controller'] | null = null;
        let resolveUpload!: (r: TypeUploadResult) => void;
        let rejectUpload!: (e: unknown) => void;
        const promise = new Promise<TypeUploadResult>((res, rej) => {
            resolveUpload = res;
            rejectUpload = rej;
        });

        const itemGate = gate;
        let resolveNextGate!: () => void;
        gate = new Promise<void>((res) => {
            resolveNextGate = res;
        });

        let gateOpened = false;
        const openGate = (): void => {
            if (!gateOpened) {
                gateOpened = true;
                resolveNextGate();
            }
        };

        /*
         * abort()/pause() called before the group starts have no inner controller to
         * act on. The request is recorded here and honoured once the group's turn comes
         * — an aborted group must never send a single request.
         */
        const requested = { aborted: false, paused: false };

        /*
         * A slot must be released exactly once, and only if it was ever acquired — releasing one
         * that was not held hands a free slot to a waiter that should still be queued.
         */
        let slotAcquired = false;
        let slotReleased = false;
        const releaseSlot = (): void => {
            if (!slotAcquired || slotReleased) {
                return;
            }

            slotReleased = true;
            slots.release();
        };

        void itemGate
            .then(async () => {
                if (requested.aborted) {
                    openGate();
                    resolveUpload(createAbortedResult(group.files));
                    return;
                }

                await slots.acquire();
                slotAcquired = true;

                if (requested.aborted) {
                    releaseSlot();
                    openGate();
                    resolveUpload(createAbortedResult(group.files));
                    return;
                }

                /*
                 * Resolved here rather than inside the send functions, so the gate hooks below can
                 * be layered on top of the caller's `merge` instead of being overridden by it.
                 */
                const baseConfig: TypeUploadConfig = merge({}, group.config);
                const config: TypeUploadConfig =
                    gateOn === 'firstFile'
                        ? createGateConfig(baseConfig, {
                              openGate,
                              fnAbortGroup: options?.fnAbortGroup,
                              getController: () => innerController,
                          })
                        : baseConfig;

                const result =
                    withinGroupConcurrency > 1
                        ? sendUpload(group.files, {
                              ...config,
                              concurrency: {
                                  enabled: true,
                                  max: withinGroupConcurrency,
                                  gateFirst: true,
                              },
                          })
                        : sendSequentialFiles(group.files, config);
                innerController = result.controller;

                if (requested.paused) {
                    innerController.pause();
                }

                void result.promise
                    .then((r) => {
                        releaseSlot();
                        openGate();
                        resolveUpload(r);
                    })
                    .catch((e: unknown) => {
                        releaseSlot();
                        openGate();
                        rejectUpload(e);
                    });
            })
            /*
             * Anything thrown while starting the group (a malformed config, a body builder that
             * blows up) would otherwise skip the slot release, the gate and both settlers, hanging
             * this group and every group queued behind it.
             */
            .catch((e: unknown) => {
                releaseSlot();
                openGate();
                rejectUpload(e);
            });

        results.push({
            promise,
            controller: {
                abort: (): void => {
                    requested.aborted = true;

                    innerController?.abort();
                },
                pause: (): void => {
                    requested.paused = true;

                    innerController?.pause();
                },
                resume: (): void => {
                    requested.paused = false;

                    innerController?.resume();
                },
                getState: (): TypeUploadState => {
                    if (innerController) {
                        return innerController.getState();
                    }
                    if (requested.aborted) {
                        return 'aborted';
                    }
                    if (requested.paused) {
                        return 'paused';
                    }

                    return 'uploading';
                },
            },
        });
    }

    return results;
}

// ----------------------------------------------------------------------------

export default sendSequentialGroups;
